import React from "react";
import * as Sentry from "@sentry/react";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error("ErrorBoundary caught:", error, info);
    // Primary: report to Sentry with full component stack context
    Sentry.captureException(error, { extra: { componentStack: info?.componentStack } });
    // Fallback: also POST to backend logs (works even without Sentry DSN configured)
    try {
      const BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:8080/api/v1";
      fetch(`${BASE_URL}/errors/frontend`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          message: error?.message || String(error),
          stack: error?.stack,
          componentStack: info?.componentStack,
          url: window.location.href,
          userAgent: navigator.userAgent,
        }),
      }).catch(() => {}); // never let error reporting break the UI
    } catch (_) {}
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-[60vh] flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <div className="h-14 w-14 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="h-7 w-7 text-destructive" />
            </div>
            <h2 className="text-xl font-bold text-foreground mb-2">Something went wrong</h2>
            <p className="text-sm text-muted-foreground mb-6">
              An unexpected error occurred. Please try refreshing the page.
            </p>
            <div className="flex gap-3 justify-center">
              <Button onClick={() => window.location.reload()} className="rounded-lg">
                Refresh Page
              </Button>
              <Button variant="outline" onClick={() => this.setState({ hasError: false, error: null })} className="rounded-lg">
                Try Again
              </Button>
            </div>
            {/* Only show error details in development — avoids leaking internals in production */}
            {import.meta.env.DEV && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">Error details (dev only)</summary>
                <pre className="mt-2 text-xs bg-secondary rounded-lg p-3 overflow-auto text-muted-foreground">
                  {this.state.error.toString()}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}